import React from "react";
import { Link } from "react-router-dom";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import { getCaseList } from "../data/cases";
import { useLanguage } from "../context/LanguageContext";
import { Sparkle } from "./Sparkle";

const ICONS = {
    "production-enterprise": BusinessRoundedIcon,
    "energy-data": RocketLaunchRoundedIcon,
};

export const UXCases = () => {
    const { t, lang } = useLanguage();
    const cases = getCaseList(lang);
    return (
        <section id="cases" data-testid="cases-section" className="relative">
            <div className="max-w-[1320px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div
                            className="font-mono-archive text-[11px] mb-4 inline-flex items-center gap-2"
                            style={{ color: "var(--muted-ink)" }}
                        >
                            <Sparkle size={12} />
                            {t("cases.eyebrow")}
                        </div>
                        <h2
                            className="font-serif-display text-[44px] sm:text-[60px] lg:text-[72px] leading-[1.02]"
                            style={{ color: "var(--ink)" }}
                        >
                            {t("cases.title.a")}
                            <span
                                className="italic"
                                style={{ color: "var(--terracotta)" }}
                            >
                                {t("cases.title.b")}
                            </span>
                            .
                        </h2>
                        <p
                            className="mt-4 max-w-[560px] text-[16px] leading-[1.65]"
                            style={{ color: "var(--ink-soft)" }}
                        >
                            {t("cases.intro")}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
                    {cases.map((c) => {
                        const Icon = ICONS[c.slug];
                        return (
                            <Link
                                to={`/cases/${c.slug}`}
                                key={c.slug}
                                data-testid={`ux-case-card-${c.slug}`}
                                className="project-card group block"
                            >
                                <div
                                    className="image-wrap aspect-[5/4] relative grain-overlay"
                                    style={{ background: "var(--cream-deep)" }}
                                >
                                    <img
                                        src={c.image}
                                        alt={c.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div
                                        className="absolute top-5 left-5 font-mono-archive text-[10px] px-2.5 py-1 rounded-full"
                                        style={{
                                            background: "rgba(26,26,26,0.78)",
                                            color: "var(--paper)",
                                            backdropFilter: "blur(6px)",
                                        }}
                                    >
                                        {c.eyebrow}
                                    </div>
                                    {Icon && (
                                        <div
                                            className="absolute bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center"
                                            style={{
                                                background: c.accent,
                                                color: "var(--paper)",
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 22 }} />
                                        </div>
                                    )}
                                </div>

                                <div className="mt-7 flex items-start justify-between gap-4">
                                    <div>
                                        <div
                                            className="font-mono-archive text-[10.5px]"
                                            style={{ color: "var(--muted-ink)" }}
                                        >
                                            {c.sector} · {c.approach}
                                        </div>
                                        <h3
                                            className="font-serif-display text-[36px] sm:text-[42px] mt-3 leading-[1.04]"
                                            style={{ color: "var(--ink)" }}
                                        >
                                            {c.title}{" "}
                                            <span
                                                className="italic"
                                                style={{ color: c.accent }}
                                            >
                                                {c.subtitle}.
                                            </span>
                                        </h3>
                                    </div>
                                    <span
                                        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:rotate-45"
                                        style={{
                                            background: "var(--ink)",
                                            color: "var(--paper)",
                                        }}
                                    >
                                        <NorthEastRoundedIcon sx={{ fontSize: 20 }} />
                                    </span>
                                </div>

                                <p
                                    className="mt-4 text-[15px] leading-[1.65] max-w-[520px]"
                                    style={{ color: "var(--ink-soft)" }}
                                >
                                    {c.teaser}
                                </p>

                                <div className="mt-5 flex flex-wrap gap-2">
                                    {c.keywords.map((k) => (
                                        <span
                                            key={k}
                                            className="text-[11px] font-medium px-2.5 py-1 rounded-full"
                                            style={{
                                                color: "var(--ink-soft)",
                                                fontFamily: "Manrope, sans-serif",
                                            }}
                                        >
                                            {k}
                                        </span>
                                    ))}
                                </div>

                                <span
                                    className="mt-6 inline-flex items-center gap-2 font-mono-archive text-[11px]"
                                    style={{ color: "var(--ink)" }}
                                >
                                    {t("cases.cardCta")}
                                    <NorthEastRoundedIcon sx={{ fontSize: 14 }} />
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
